<form [formGroup]="form" *transloco="let t" (ngSubmit)="requestJob()" bgoScrollToInvalidOnSubmit>
  <div class="pb-32">
    <h1 class="heading-xl">{{ t('createJobRequest.summary.title') }}</h1>
    <div class="mt-2xs text-md">{{ t('createJobRequest.summary.subtitle') }}</div>
    <div class="mt-xl grid grid-cols-1 gap-8 sm:grid-cols-12">
      <div class="space-y-lg sm:col-span-6">
        <div class="heading-md">{{ t('createJobRequest.jobRequestsLabel') }}</div>
        <bgo-job-request-summary-card
          [priceRange]="priceRange$ | async"
          [numberOfItems]="numberOfItems"
          [offering]="offering || {label: jobRequestState.detail.title || '', type: OfferingType.OfferBased}"
          [showBreakdown]="false"
          [editLink]="editDetail.sameLevelPath"
        ></bgo-job-request-summary-card>
        @if (jobRequestState.detail.voucherCode; as voucher) {
          <bgo-job-request-fragment-summary-card
            [icon]="voucherIcon"
            [editPath]="editDetail.sameLevelPath"
            [fragment]="VoucherAnchorTag"
          >
            <div class="heading-sm">
              {{ voucher }}
            </div>

            <div class="text-xs">{{ t('createJobRequest.summary.voucherSummary.helpText') }}</div>
          </bgo-job-request-fragment-summary-card>
        }
        @if (jobRequestState.damage) {
          <div class="mt-xl space-y-lg">
            <div class="heading-md">
              {{ t('createJobRequest.summary.damage') }}
            </div>
            <bgo-job-request-fragment-summary-card [icon]="damageIcon" [editPath]="editDetail.sameLevelPath">
              <div class="heading-sm">{{ jobRequestState.damage.caseNumber }}</div>
              <div class="heading-sm">{{ jobRequestState.damage.occurredAt | date: 'dd.MM.yyyy' }}</div>
              <div class="text-xs">
                {{ t('createJobRequest.summary.caseNr') }}
              </div>
            </bgo-job-request-fragment-summary-card>
          </div>
        }
        <div class="mt-xl space-y-lg">
          <div class="heading-md">
            {{ t('createJobRequest.summary.filesSummary.title') }}
          </div>
          <bgo-job-request-fragment-summary-card
            class="w-full"
            [icon]="filesIcon"
            [editPath]="editDetail.sameLevelPath"
            [fragment]="PhotosAnchorTag"
          >
            @if (jobRequestState.attachedFiles.files?.length; as count) {
              <div class="heading-sm">
                <span class="font-bold text-grey-700">{{ count }}</span>
                {{ t('createJobRequest.summary.filesSummary.whenSpecified.label') }}
              </div>
              <div class="text-xs">
                {{ t('createJobRequest.summary.filesSummary.whenSpecified.helpText') }}
              </div>
            } @else {
              <div class="heading-sm">
                {{ t('createJobRequest.summary.filesSummary.whenNotSpecified.label') }}
              </div>
              <div class="text-xs">
                {{ t('createJobRequest.summary.filesSummary.whenNotSpecified.helpText') }}
              </div>
            }
          </bgo-job-request-fragment-summary-card>
        </div>
        <div class="mt-xl space-y-lg">
          <div class="heading-md">
            {{ t('createJobRequest.summary.executionTime') }}
          </div>
          <bgo-job-request-fragment-summary-card [icon]="calendarIcon" [editPath]="editTimeFrame.sameLevelPath">
            <div class="heading-sm">
              {{
                jobRequestState.executionDetails.expectedExecution
                  | translateNested: 'createJobRequest.predefinedExecutionList'
              }}
            </div>
            @if (jobRequestState.executionDetails.latestPossibleDate) {
              <div class="text-xs">
                {{ t('createJobRequest.summary.latestExecutionDate') }}
                {{ jobRequestState.executionDetails.latestPossibleDate | date: 'dd.MM.yyyy' }}
              </div>
            }
          </bgo-job-request-fragment-summary-card>
        </div>
        <div class="mt-xl space-y-lg">
          <div class="heading-md">
            {{ t('createJobRequest.summary.executionContact') }}
          </div>
          <bgo-job-request-fragment-summary-card [icon]="addressIcon" [editPath]="editContact.sameLevelPath">
            @if (jobRequestState.contact.company) {
              <div class="heading-sm">
                {{ jobRequestState.contact.company }}
              </div>
            }
            @if (jobRequestState.contact) {
              <div class="heading-sm">
                {{ jobRequestState.contact | bgoFullName }}
              </div>
            }
            <div *ngTemplateOutlet="executionAddress"></div>
          </bgo-job-request-fragment-summary-card>

          <bgo-job-request-fragment-summary-card [icon]="phoneIcon" [editPath]="editContact.sameLevelPath">
            <div class="heading-sm">+41 {{ jobRequestState.contact.phoneNumber.replace('+41', '') }}</div>
            <div class="text-xs">{{ t('createJobRequest.contact.phoneNumber') }}</div>
          </bgo-job-request-fragment-summary-card>

          <bgo-job-request-fragment-summary-card [icon]="emailIcon" [editPath]="editContact.sameLevelPath">
            <div class="heading-sm">{{ jobRequestState.contact.email }}</div>
            <div class="text-xs">{{ t('createJobRequest.contact.email') }}</div>
          </bgo-job-request-fragment-summary-card>
        </div>
        @if (jobRequestState.billingDetails.billingAddressNotIdentical) {
          <div class="mt-xl space-y-lg">
            <div class="heading-md">
              {{ t('createJobRequest.summary.billingContact') }}
            </div>
            <bgo-job-request-fragment-summary-card [icon]="addressIcon" [editPath]="editContact.sameLevelPath">
              @if (jobRequestState.billingDetails.billingClient?.company; as company) {
                <div class="heading-sm">
                  {{ company }}
                </div>
              }
              @if (jobRequestState.billingDetails.billingAddress) {
                <div class="heading-sm">
                  {{ jobRequestState.billingDetails.billingClient | bgoFullName }}
                </div>
              }
              <div *ngTemplateOutlet="billingAddress"></div>
            </bgo-job-request-fragment-summary-card>
            <bgo-job-request-fragment-summary-card [icon]="phoneIcon" [editPath]="editContact.sameLevelPath">
              @if (jobRequestState.billingDetails.billingClient?.phoneNumber; as tel) {
                <div class="heading-sm">
                  {{ tel | bgoSwissPhoneNumber }}
                </div>
              }
              <div class="text-xs">{{ t('createJobRequest.contact.phoneNumber') }}</div>
            </bgo-job-request-fragment-summary-card>
            <bgo-job-request-fragment-summary-card [icon]="emailIcon" [editPath]="editContact.sameLevelPath">
              <div class="heading-sm">{{ jobRequestState.billingDetails.billingClient?.email }}</div>
              <div class="text-xs">{{ t('createJobRequest.contact.email') }}</div>
            </bgo-job-request-fragment-summary-card>
          </div>
        }
      </div>
      <div class="sm:col-span-5 sm:col-start-8">
        <div class="sticky top-32">
          <!-- standard support card -->
          @if (!jobRequestState.damage) {
            <cft-support-box
              [contact]="{
                name: t('createJobRequest.submitted.contactName'),
                role: t('createJobRequest.submitted.contactRole'),
                phone: t('createJobRequest.submitted.contactPhone'),
                email: t('createJobRequest.submitted.contactEmail'),
              }"
              [description]="t('createJobRequest.submitted.description')"
            >
              <cft-support-portrait
                [name]="t('createJobRequest.submitted.contactName')"
                [imageUrl]="'/assets/images/team/' + t('createJobRequest.submitted.photo')"
              ></cft-support-portrait>
            </cft-support-box>
          }
          <!-- damage contact info -->
          @if (jobRequestState.damage && agent) {
            <bgo-job-agent-support-box
              [agent]="agent"
              [agency]="agent.agency"
              [logo]="agency?.logo ?? agent.agency?.logo"
              [damageCaseNumber]="jobRequestState.damage.caseNumber"
            ></bgo-job-agent-support-box>
          }
        </div>
      </div>
    </div>
    @if (formConfig?.shouldAskClientExpectation) {
      <div class="mt-xl space-y-lg md:w-2/3 lg:w-1/2 2xl:w-1/3">
        <div class="heading-md">
          {{ t('createJobRequest.summary.expectation.title') }}
        </div>
        <cft-checkbox [formControl]="clientExpectationControls.consulting">
          {{ t('createJobRequest.summary.expectation.consulting') }}
        </cft-checkbox>
        <cft-checkbox [formControl]="clientExpectationControls.estimation">
          {{ t('createJobRequest.summary.expectation.estimation') }}
        </cft-checkbox>
        <cft-checkbox [formControl]="clientExpectationControls.offer">
          {{ t('createJobRequest.summary.expectation.offer') }}
        </cft-checkbox>
        <cft-checkbox [formControl]="clientExpectationControls.execution">
          {{ t('createJobRequest.summary.expectation.execution') }}
        </cft-checkbox>
        @if (controls.clientExpectation.errors?.atLeastOneOptionSelected && controls.clientExpectation.touched) {
          <div class="error">
            {{ t('createJobRequest.summary.expectation.validationError') }}
          </div>
        }
      </div>
    }
    @if (formConfig?.shouldAskAgentExpectation) {
      <div class="mt-xl flex flex-col">
        <div class="heading-md">
          {{ t('createJobRequest.summary.agentExpectation.title') }}
        </div>
        <cft-radio [formControl]="agentExpectationControls.offerValidationType" [value]="'MANUAL'" class="mt-lg">
          {{ t('createJobRequest.summary.agentExpectation.manualOfferValidation') }}
        </cft-radio>
        <cft-radio
          [formControl]="agentExpectationControls.offerValidationType"
          [value]="'AUTO_CLIENT_DEDUCTIBLE'"
          class="mt-sm"
        >
          {{ t('createJobRequest.summary.agentExpectation.autoOfferValidationDeductible') }}
        </cft-radio>
        @if (agentExpectationControls.offerValidationType.value === 'AUTO_CLIENT_DEDUCTIBLE') {
          <cft-form-field class="md:w-1/2">
            <label for="deductible">{{ t('createJobRequest.summary.agentExpectation.deductibleLabel') }}</label>
            <input type="number" id="deductible" [formControl]="agentExpectationControls.deductible" />
          </cft-form-field>
        }
        <cft-radio
          [formControl]="agentExpectationControls.offerValidationType"
          [value]="'AUTO_DAMAGE_COVERED'"
          class="mt-sm"
        >
          {{ t('createJobRequest.summary.agentExpectation.autoOfferValidationCoverage') }}
        </cft-radio>
        @if (agentExpectationControls.offerValidationType.value === 'AUTO_DAMAGE_COVERED') {
          <cft-form-field class="md:w-1/2">
            <label for="coverage">{{ t('createJobRequest.summary.agentExpectation.coverageLabel') }}</label>
            <input type="number" id="coverage" [formControl]="agentExpectationControls.coverage" />
          </cft-form-field>
        }
        @if (isControlInvalid(agentExpectationControls.offerValidationType)) {
          <div class="mt-sm ml-xs flex text-sm text-vermillon-500">
            {{ t('createJobRequest.summary.agentExpectation.required') }}
          </div>
        }
      </div>
    }
    <div class="mt-xl md:w-2/3 lg:w-2/3 xl:w-1/2">
      @if (!formConfig?.canSkipTermsAndConditions) {
        <div class="heading-md">
          {{ t('createJobRequest.summary.termsAndConditions') }}
        </div>
        <p
          class="terms-and-conditions"
          data-testid="terms-and-conditions"
          [innerHTML]="
            t('createJobRequest.summary.agb', {
              linkToTAndC: linkService.localizedPathTo(APPLICATION_ROUTES.CLIENT_TERMS_AND_CONDITIONS),
              linkToDataProtection: linkService.localizedPathTo(APPLICATION_ROUTES.DATA_PRIVACY),
            })
          "
        ></p>
      }
      @if (submitJobRequestError) {
        <div class="mt-6">
          <bgo-error-banner>
            @switch (submitJobRequestError) {
              @case (RequestJobError.InvalidEmail) {
                <a [routerLink]="editContact.sameLevelPath" class="cursor-pointer" queryParamsHandling="merge">
                  {{ t('createJobRequest.errors.invalidEmail') }}
                </a>
              }
              @case (RequestJobError.InvalidPhoneNumber) {
                <a [routerLink]="editContact.sameLevelPath" class="cursor-pointer" queryParamsHandling="merge">
                  {{ t('createJobRequest.errors.invalidPhoneNumber') }}
                </a>
              }
              @case (RequestJobError.InvalidBillingPostalCode) {
                <a [routerLink]="editContact.sameLevelPath" class="cursor-pointer" queryParamsHandling="merge">
                  {{ t('createJobRequest.errors.invalidBillingPostalCode') }}
                </a>
              }
              @case (RequestJobError.InvalidAddressPostalCode) {
                <a [routerLink]="editContact.sameLevelPath" class="cursor-pointer" queryParamsHandling="merge">
                  {{ t('createJobRequest.errors.invalidAddressPostalCode') }}
                </a>
              }
              @case (RequestJobError.EmailLinkedToAnotherAccount) {
                <a [routerLink]="editContact.sameLevelPath" class="cursor-pointer" queryParamsHandling="merge">
                  {{ t('createJobRequest.errors.emailLinkedToAnotherAccount') }}
                </a>
              }
            }
          </bgo-error-banner>
        </div>
      }
    </div>
    <bgo-job-request-footer
      (cancelButtonClick)="jobRequestService.askConfirmationToCloseProcess()"
      (backButtonClick)="previous()"
      [nextButtonText]="t('createJobRequest.summary.submit')"
      [nextButtonLoading$]="isRequestingJob$"
      [showBackButton]="false"
      [progressSteps]="progressSteps"
      [showPoweredByBuildigo]="!!agency?.logo"
    ></bgo-job-request-footer>
  </div>
  <ng-template #executionAddress>
    @if (jobRequestState.executionDetails.executionAddress) {
      <div class="text-xs">
        <div>{{ jobRequestState.executionDetails.executionAddress.streetAndHouseNumber }}</div>
        <div>{{ jobRequestState.executionDetails.executionAddress | bgoFullLocation }}</div>
      </div>
    }
  </ng-template>
  <ng-template #billingAddress>
    <div class="text-xs">
      <div>{{ jobRequestState.billingDetails.billingAddress?.streetAndHouseNumber }}</div>
      <div>{{ jobRequestState.billingDetails.billingAddress | bgoFullLocation }}</div>
    </div>
  </ng-template>
</form>
